import { Component, inject, input, OnInit, output, Resource, signal } from "@angular/core";
import { ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatChipsModule } from "@angular/material/chips";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";

@Component({
  selector: 'app-form-control-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatIconModule,
    MatChipsModule
  ],
})
export class FormControlAutocompleteComponent<T extends { id: string } | string>
  implements ControlValueAccessor, OnInit {
  ngControl = inject(NgControl);

  label = input.required<string>()
  resource = input.required<Resource<T[] | undefined>>();
  multiple = input(false);
  displayWith = input((o: T): string => this.identify(o));

  searchString = output<string>();

  values = signal<T[]>([]);
  isRequired = signal(false);
  formControl = new FormControl<T[]>([]);

  private onChange: ((value: T | T[] | null) => void) | null = null;
  private onTouched: (() => void) | null = null;

  constructor() {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit() {
    const parentFormControl = this.ngControl.control as FormControl;
    this.formControl.setValidators(parentFormControl.validator);

    this.isRequired.set(parentFormControl.hasValidator(Validators.required));
  }

  addValue(value: T) {
    if (this.multiple()) {
      const currentValues = this.values();
      if (currentValues.findIndex(v => this.identify(v) === this.identify(value)) !== -1) {
        return;
      }
      currentValues.push(value);

      this.setValues(currentValues);
    } else {
      this.setValues([value]);
    }
  }

  removeValue(value: T) {
    const currentValues = this.values().filter(v => this.identify(v) !== this.identify(value));
    if (currentValues.length === this.values().length) {
      return;
    }

    this.setValues(currentValues);
  }

  setValues(values: T[]) {
    this.values.set(values);

    if (this.onChange) {
      const emittedValue = this.multiple() ? values : values.length ? values[0] : null;
      this.onChange(emittedValue);
    }

    if (this.onTouched) {
      this.onTouched();
    }

    this.formControl.setValue(values);
  }

  onSearchEvent(event: Event) {
    const searchString = (event.target as HTMLInputElement).value || '';
    this.searchString.emit(searchString);
  }

  identify(o: T): string {
    if (typeof o === "string") {
      return o;
    }

    if (o?.id) {
      return o.id;
    }

    return `${o}`;
  }

  // ControlValueAccessor

  writeValue(obj: T | T[] | null): void {
    if (obj === null) {
      obj = [];
    } else if (!Array.isArray(obj)) {
      obj = [obj] as T[];
    }

    this.values.set(obj);
    this.formControl.setValue(obj);
  }

  registerOnChange(fn: ((value: T | T[] | null) => void) | null): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: (() => void) | null): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }
}
